import { useQueryClient } from '@tanstack/react-query';
import { ComponentProps, useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { CurrencyFilterSelector } from 'src/components/Selectors/CurrencyFilterSelector';
import { useAppContext } from 'src/contexts/AppContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { DatePickerInput } from 'src/core/POS/DateSelector';
import { PosCurrencyField } from 'src/core/POS/PosCurrencyField';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Button, Stack } from 'src/core/ui';
import { useAccessibleMarketplaces } from 'src/hooks/useAccessibleMarketplaces';
import { ContentId } from 'src/utils/constants/contentId';
import { MARKET_PLACE_TO_CID } from 'src/utils/constants/contentIdMaps';
import { getLocaleFromLanguageOrCurrent } from 'src/utils/localeUtils';
import { posChangedField, posField } from 'src/utils/posFieldUtils';
import {
  Marketplace,
  MarketplacePaymentInput,
  MarketplaceSalePayment,
} from 'src/WebApiController';

export type EditMarketplacePaymentDialogProps = Omit<
  ComponentProps<typeof GenericDialog>,
  'header' | 'footer'
> & {
  onSubmit: (data: MarketplacePaymentInput) => Promise<void>;
  onCancel: () => void;
  marketplacePaymentData: MarketplaceSalePayment;
};

export const EditMarketplacePaymentDialog = ({
  marketplacePaymentData,
  onSubmit,
  onCancel,
  ...dialogProps
}: EditMarketplacePaymentDialogProps) => {
  const requiredText = useContent(ContentId.Required);
  const [isLoading, setIsLoading] = useState(false);
  const { getUiCurrency } = useLocalizationContext();
  const { loginContext, activeAccountWebClientConfig } = useAppContext();
  const accessibleMarketplaces = useAccessibleMarketplaces();

  const {
    register,
    setValue,
    control,
    watch,
    formState,
    getValues,
    reset,
    handleSubmit,
  } = useForm<MarketplacePaymentInput>({
    defaultValues: {
      marketplacePaymentId: marketplacePaymentData.id,
      externalPaymentId: posField(marketplacePaymentData.extPmtId),
      marketplace: posField(marketplacePaymentData.mkp),
      paymentDate: posField(marketplacePaymentData.pmtDate),
      paymentReferenceId: posField(marketplacePaymentData.pmtRefId),
      paymentCurrency: posField(marketplacePaymentData.pmtAmt.currency),
      manualPaymentAmount: posField(
        marketplacePaymentData.manualPmtAmt?.amt ??
          marketplacePaymentData.pmtAmt.amt
      ),
      manualProceedsAmount: posField(
        marketplacePaymentData.manualProceedsAmt?.amt ??
          marketplacePaymentData.proceedsAmt?.amt
      ),
      manualCreditAmount: posField(
        marketplacePaymentData.manualCreditAmt?.amt ??
          marketplacePaymentData.credit?.amt
      ),
      manualChargeAmount: posField(
        marketplacePaymentData.manualChargeAmt?.amt ??
          marketplacePaymentData.charge?.amt
      ),
    },
  });

  const currencyCode = watch('paymentCurrency')?.value;
  const manualPaymentAmount = watch('manualPaymentAmount')?.value;
  const manualProceedsAmount = watch('manualProceedsAmount')?.value;
  const manualCreditAmount = watch('manualCreditAmount')?.value;
  const manualChargeAmount = watch('manualChargeAmount')?.value;

  const hasErrors = Object.keys(formState.errors).length > 0;
  const queryClient = useQueryClient();

  const handleFormSubmit = useCallback(async () => {
    const input = getValues();
    setIsLoading(true);
    try {
      await onSubmit(input);
    } catch (error) {
      // Error is handled in the parent component
    } finally {
      setIsLoading(false);
    }
  }, [getValues, onSubmit]);

  return (
    <GenericDialog
      header={<Content id={ContentId.EditMarketplacePayment} />}
      footer={
        <>
          <Button
            variant="outline"
            disabled={isLoading || hasErrors}
            onClick={onCancel}
          >
            <Content id={ContentId.Cancel} />
          </Button>
          <Button
            disabled={isLoading || hasErrors}
            onClick={() => {
              handleSubmit(handleFormSubmit)();
            }}
          >
            <Content id={ContentId.Save} />
          </Button>
        </>
      }
      {...dialogProps}
      isLoading={isLoading}
      size="xl"
      onOpened={() => {
        reset();
      }}
      onClosed={() => {
        reset();
      }}
    >
      <Stack direction="column" gap="xl">
        <PosFormField
          label={<Content id={ContentId.Marketplace} />}
          errors={formState.errors.marketplace?.message}
        >
          <Controller
            name="marketplace"
            rules={{ required: requiredText }}
            control={control}
            render={({ field }) => (
              <PosEnumSelect
                style={{ width: '100%' }}
                placeholderText={ContentId.Select}
                enableEmptySelection
                onChange={(marketplace) => {
                  if (marketplace) {
                    setValue('marketplace', posChangedField(marketplace));
                  }
                }}
                valueOptionsContent={(
                  accessibleMarketplaces ?? [Marketplace.StubHub]
                )
                  .concat(Marketplace.Offline)
                  .reduce(
                    (cur, m) => {
                      cur[m] = MARKET_PLACE_TO_CID[m];
                      return cur;
                    },
                    {} as Record<Marketplace, string>
                  )}
                value={field.value?.value ?? null}
                name={field.name}
                ref={field.ref}
                onBlur={field.onBlur}
              />
            )}
          />
        </PosFormField>

        <PosFormField
          label={<Content id={ContentId.PaymentDate} />}
          errors={formState.errors.paymentDate?.message}
        >
          <Controller
            name="paymentDate"
            rules={{ required: requiredText }}
            control={control}
            render={({ field }) => (
              <DatePickerInput
                fieldError={formState.errors.paymentDate?.message}
                locale={getLocaleFromLanguageOrCurrent()}
                value={field.value?.value ? new Date(field.value.value) : null}
                onDateChange={(d) => {
                  setValue('paymentDate', posChangedField(d.toISOString()));
                }}
              />
            )}
          />
        </PosFormField>

        <PosFormField label={<Content id={ContentId.ExternalPaymentId} />}>
          <PosTextField
            {...register('externalPaymentId.value', {
              required: { value: true, message: requiredText },
              onChange: ({ target: { value } }) => {
                setValue('externalPaymentId', posChangedField(value));
              },
            })}
          />
        </PosFormField>

        <PosFormField label={<Content id={ContentId.PaymentAmount} />}>
          <PosCurrencyField
            uiCurrency={getUiCurrency(getValues(`paymentCurrency.value`))}
            value={manualPaymentAmount || undefined}
            {...register(`manualPaymentAmount.value`, {
              valueAsNumber: true,
              onChange: ({ target: { value } }) => {
                setValue('manualPaymentAmount', posChangedField(value));
              },
              required: false,
            })}
          />
        </PosFormField>
        <PosFormField label={<Content id={ContentId.ProceedsAmount} />}>
          <PosCurrencyField
            uiCurrency={getUiCurrency(getValues(`paymentCurrency.value`))}
            value={manualProceedsAmount || undefined}
            {...register(`manualProceedsAmount.value`, {
              valueAsNumber: true,
              onChange: ({ target: { value } }) => {
                setValue('manualProceedsAmount', posChangedField(value));
              },
              required: false,
            })}
          />
        </PosFormField>
        <PosFormField label={<Content id={ContentId.CreditAmount} />}>
          <PosCurrencyField
            uiCurrency={getUiCurrency(getValues(`paymentCurrency.value`))}
            value={manualCreditAmount || undefined}
            {...register(`manualCreditAmount.value`, {
              valueAsNumber: true,
              onChange: ({ target: { value } }) => {
                setValue('manualCreditAmount', posChangedField(value));
              },
              required: false,
            })}
          />
        </PosFormField>
        <PosFormField label={<Content id={ContentId.ChargeAmount} />}>
          <PosCurrencyField
            uiCurrency={getUiCurrency(getValues(`paymentCurrency.value`))}
            value={manualChargeAmount || undefined}
            {...register(`manualChargeAmount.value`, {
              valueAsNumber: true,
              onChange: ({ target: { value } }) => {
                setValue('manualChargeAmount', posChangedField(value));
              },
              required: false,
            })}
          />
        </PosFormField>

        <PosFormField label={<Content id={ContentId.PaymentCurrency} />}>
          <Controller
            control={control}
            name="paymentCurrency.value"
            rules={{ required: { value: true, message: requiredText } }}
            render={({ field: { onChange, value }, fieldState }) => (
              <CurrencyFilterSelector
                showFullWidth={true}
                value={value || ''}
                defaultValue={currencyCode || ''}
                onChange={(newCurrencyCode) => {
                  if (newCurrencyCode && newCurrencyCode !== value) {
                    setValue(
                      'paymentCurrency',
                      posChangedField(newCurrencyCode)
                    );
                    onChange(newCurrencyCode);
                  }
                }}
              />
            )}
          />
        </PosFormField>

        <PosFormField label={<Content id={ContentId.PaymentReferenceID} />}>
          <PosTextField
            {...register('paymentReferenceId.value', {
              onChange: ({ target: { value } }) => {
                setValue('paymentReferenceId', posChangedField(value));
              },

              required: false,
            })}
          />
        </PosFormField>
      </Stack>
    </GenericDialog>
  );
};
